import React, { useEffect, useRef } from 'react';
import { Heart, X, Coffee, CreditCard } from 'lucide-react';

const PayPalIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
  </svg>
);

const BuyMeCoffeeIcon = ({ size = 20 }) => (
  <Coffee size={size} />
);

export default function SupportModal({ isOpen, onClose }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const onOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  return (
    <div
      ref={overlayRef}
      onClick={onOverlayClick}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly"
      aria-modal="true"
      role="dialog"
    >
      <div className="relative w-full max-w-sm animate-slideUp">
        <div className="card-elegant p-6 shadow-2xl border border-white/40 bg-white/95 backdrop-blur rounded-2xl">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 text-slate-400 hover:text-slate-700 flex items-center justify-center rounded-full hover:bg-slate-100 transition-all"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <Heart size={18} className="text-rose-400" />
              <h3 className="text-lg font-display font-semibold text-slate-800">Support MarkerSync</h3>
            </div>
            <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full" />
          </div>

          <p className="text-sm text-slate-500 mb-5 leading-relaxed">
            MarkerSync is free and built with love. If it's been useful to you, a small contribution helps keep it running and growing.
          </p>

          <div className="space-y-3">
            <a
              href="https://buymeacoffee.com/markersync"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-semibold text-sm transition-all hover:scale-[1.02] shadow-sm hover:shadow-md"
            >
              <BuyMeCoffeeIcon size={18} />
              <div className="text-left flex-1">
                <div className="font-semibold">Buy Me a Coffee</div>
                <div className="text-xs font-normal opacity-75">buymeacoffee.com/markersync</div>
              </div>
            </a>

            <a
              href="https://paypal.me/markersync"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm transition-all hover:scale-[1.02] shadow-sm hover:shadow-md"
            >
              <PayPalIcon size={18} />
              <div className="text-left flex-1">
                <div className="font-semibold">PayPal</div>
                <div className="text-xs font-normal opacity-75">paypal.me/markersync</div>
              </div>
            </a>
          </div>

          <p className="text-[11px] text-slate-400 text-center mt-4">
            Every contribution makes a difference — thank you! 💛
          </p>
        </div>
      </div>
    </div>
  );
}
